import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import CustomAppBar from "../components/User/Appbar";
import Sidebar from "../components/User/Sidebar";
import IndianQrPaymentModal from "../components/Payment/IndianQrPaymentModal";
import { useUserSession } from "../hooks/useUserSession";
import {
  getPayableServices,
  getServiceLabel,
  getServiceStatusLabel,
  hasActiveCare,
} from "../utils/careAccess";
import { startServicePayment } from "../utils/startServicePayment";
import { isIndianUser } from "../utils/paymentCountry";
import "../components/App.css";

const UnlockDashboard = () => {
  const { loading, userDetails, setUserDetails, refreshUser } = useUserSession();
  const navigate = useNavigate();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");
  const [indianModalOpen, setIndianModalOpen] = useState(false);
  const [indianPaymentService, setIndianPaymentService] = useState(null);
  const location = useLocation();

  const payableServices = useMemo(
    () => getPayableServices(userDetails),
    [userDetails]
  );

  const selectedService = useMemo(
    () =>
      payableServices.find((service) => service._id === selectedServiceId) ||
      null,
    [payableServices, selectedServiceId]
  );

  useEffect(() => {
    if (payableServices.length > 0 && !selectedServiceId) {
      setSelectedServiceId(payableServices[0]._id);
    }
  }, [payableServices, selectedServiceId]);

  const handlePayNow = async () => {
    if (!selectedService) {
      setError("Please select a service to pay for.");
      return;
    }

    setPaying(true);
    setError("");

    try {
      await startServicePayment(selectedService, userDetails?.country, {
        onIndianPayment: (service) => {
          setIndianPaymentService(service);
          setIndianModalOpen(true);
          setPaying(false);
        },
      });
    } catch (checkoutError) {
      console.error("Error starting payment:", checkoutError);
      setError(
        checkoutError.response?.data?.error ||
          checkoutError.message ||
          "Unable to start payment. Please try again."
      );
      setPaying(false);
    }
  };

  const handleIndianPaymentSuccess = async () => {
    await refreshUser();
    const redirectTo = location.state?.from || "/user/dashboard";
    navigate(redirectTo, { replace: true });
  };

  if (!loading && hasActiveCare(userDetails)) {
    const redirectTo = location.state?.from || "/user/dashboard";
    return <Navigate to={redirectTo} replace />;
  }

  return (
    <div className="standalone-dashboard-page">
      <CustomAppBar
        handleDrawerOpen={() => setOpenDrawer(true)}
        userDetails={userDetails}
        setUserDetails={setUserDetails}
      />
      <Sidebar
        variant="temporary"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        onNavClick={() => setOpenDrawer(false)}
        userDetails={userDetails}
      />
      <Container>
        <Box className="unlock-dashboard-page">
          {loading ? (
            <Box className="unlock-dashboard-loading">
              <CircularProgress color="success" />
            </Box>
          ) : (
            <Paper elevation={0} className="unlock-dashboard-card">
              <Typography variant="overline" className="unlock-dashboard-eyebrow">
                Dashboard access required
              </Typography>
              <Typography variant="h4" className="unlock-dashboard-title">
                Unlock your Bevogtet dashboard
              </Typography>
              <Typography
                variant="body1"
                className="unlock-dashboard-subtitle"
                sx={{ display: "block", mb: 6 }}
              >
                Choose an unpaid or expired care service below, then continue to
                {isIndianUser(userDetails?.country)
                  ? " scan the QR code and submit your payment proof."
                  : " secure checkout to activate your dashboard features."}
              </Typography>

              {payableServices.length > 0 ? (
                <>
                  <FormControl
                    fullWidth
                    className="unlock-dashboard-select"
                    sx={{ mt: 1 }}
                  >
                    <InputLabel id="payable-service-label">
                      Select service
                    </InputLabel>
                    <Select
                      labelId="payable-service-label"
                      label="Select service"
                      value={selectedServiceId}
                      onChange={(event) =>
                        setSelectedServiceId(event.target.value)
                      }
                    >
                      {payableServices.map((service) => (
                        <MenuItem key={service._id} value={service._id}>
                          {getServiceLabel(service)} -{" "}
                          {getServiceStatusLabel(service)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {selectedService && (
                    <Box className="unlock-dashboard-summary">
                      <div>
                        <span>Service</span>
                        <strong>{getServiceLabel(selectedService)}</strong>
                      </div>
                      <div>
                        <span>Status</span>
                        <strong>{getServiceStatusLabel(selectedService)}</strong>
                      </div>
                      <div>
                        <span>Care fees</span>
                        <strong>
                          {selectedService.careFees} {selectedService.currency}
                        </strong>
                      </div>
                    </Box>
                  )}

                  <Button
                    variant="contained"
                    color="success"
                    size="large"
                    className="unlock-dashboard-pay-btn"
                    onClick={handlePayNow}
                    disabled={paying || !selectedService}
                    startIcon={
                      paying ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : null
                    }
                  >
                    {paying ? "Starting payment..." : "Pay Now"}
                  </Button>
                </>
              ) : (
                <Alert severity="info" className="unlock-dashboard-alert">
                  You do not have any unpaid or expired services yet. Purchase a
                  care plan first to continue.
                </Alert>
              )}

              {error && (
                <Alert severity="error" className="unlock-dashboard-alert">
                  {error}
                </Alert>
              )}

              <Box className="unlock-dashboard-links">
                <Button
                  component={Link}
                  to="/user/purchase-care-plan"
                  variant="outlined"
                  color="success"
                >
                  Purchase Care Plan
                </Button>
              </Box>
            </Paper>
          )}
        </Box>
      </Container>
      <IndianQrPaymentModal
        open={indianModalOpen}
        onClose={() => setIndianModalOpen(false)}
        service={indianPaymentService}
        onSuccess={handleIndianPaymentSuccess}
      />
    </div>
  );
};

export default UnlockDashboard;
